import type { Edge, Node } from '@xyflow/react';

/** Stored in Hub `draftGraph` / `publishedGraph` (Agent Builder–style envelope). */
export interface AgentWorkflowDraftNode {
  id: string;
  type: string;
  label?: string;
  position: { x: number; y: number };
  config?: Record<string, unknown>;
}

export interface AgentWorkflowDraftEdge {
  id: string;
  type?: string;
  source: string;
  target: string;
  config?: Record<string, unknown>;
  priority?: number;
}

export interface AgentWorkflowDraftGraph {
  nodes: AgentWorkflowDraftNode[];
  edges: AgentWorkflowDraftEdge[];
  meta?: Record<string, unknown>;
}

const EMPTY_GRAPH: AgentWorkflowDraftGraph = { nodes: [], edges: [], meta: {} };

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Normalize API jsonb (may be `{}` or partial) into a draft graph. */
export function parseDraftGraph(raw: Record<string, unknown> | null | undefined): AgentWorkflowDraftGraph {
  if (!raw || !isRecord(raw)) return { ...EMPTY_GRAPH, nodes: [], edges: [] };
  const nodesRaw = raw.nodes;
  const edgesRaw = raw.edges;
  const metaRaw = raw.meta;
  const nodes: AgentWorkflowDraftNode[] = Array.isArray(nodesRaw)
    ? nodesRaw
        .map((n) => {
          if (!isRecord(n)) return null;
          const id = typeof n.id === 'string' ? n.id : null;
          const type = typeof n.type === 'string' ? n.type : 'step';
          const label = typeof n.label === 'string' ? n.label : undefined;
          const pos = n.position;
          let position = { x: 0, y: 0 };
          if (isRecord(pos)) {
            const x = typeof pos.x === 'number' ? pos.x : Number(pos.x) || 0;
            const y = typeof pos.y === 'number' ? pos.y : Number(pos.y) || 0;
            position = { x, y };
          }
          const config = n.config;
          if (!id) return null;
          return {
            id,
            type,
            label,
            position,
            config: isRecord(config) ? config : undefined,
          } as AgentWorkflowDraftNode;
        })
        .filter((x): x is AgentWorkflowDraftNode => x != null)
    : [];
  const edges: AgentWorkflowDraftEdge[] = Array.isArray(edgesRaw)
    ? edgesRaw
        .map((e): AgentWorkflowDraftEdge | null => {
          if (!isRecord(e)) return null;
          const id = typeof e.id === 'string' ? e.id : null;
          const type = typeof e.type === 'string' ? e.type : undefined;
          const source = typeof e.source === 'string' ? e.source : null;
          const target = typeof e.target === 'string' ? e.target : null;
          const priority =
            typeof e.priority === 'number' ? e.priority : Number.isFinite(Number(e.priority)) ? Number(e.priority) : undefined;
          const config = isRecord(e.config) ? e.config : undefined;
          if (!id || !source || !target) return null;
          return { id, type, source, target, config, priority };
        })
        .filter((x): x is AgentWorkflowDraftEdge => x != null)
    : [];
  const meta = isRecord(metaRaw) ? { ...metaRaw } : {};
  return { nodes, edges, meta };
}

export function draftGraphToReactFlow(graph: AgentWorkflowDraftGraph): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = graph.nodes.map((n) => {
    const label = n.label ?? n.type;
    const preview =
      n.config && typeof n.config.summary === 'string'
        ? String(n.config.summary).slice(0, 48)
        : undefined;
    return {
      id: n.id,
      type: 'flowNode',
      position: { ...n.position },
      data: {
        label,
        backendType: n.type,
        config: n.config ?? null,
        preview,
      },
    };
  });
  const edges: Edge[] = graph.edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    sourceHandle: typeof e.config?.sourceSlot === 'string' ? e.config.sourceSlot : undefined,
    targetHandle: typeof e.config?.targetSlot === 'string' ? e.config.targetSlot : undefined,
    data: {
      edgeType: e.type ?? 'default',
      config: e.config ?? null,
      priority: e.priority ?? null,
    },
  }));
  return { nodes, edges };
}

export function reactFlowToDraftGraph(
  nodes: Node[],
  edges: Edge[],
  previousMeta?: Record<string, unknown>,
): AgentWorkflowDraftGraph {
  const draftNodes: AgentWorkflowDraftNode[] = nodes.map((n) => {
    const data = (n.data ?? {}) as Record<string, unknown>;
    const backendType = typeof data.backendType === 'string' ? data.backendType : 'step';
    const label = typeof data.label === 'string' ? data.label : backendType;
    const config = data.config;
    return {
      id: n.id,
      type: backendType,
      label,
      position: { x: n.position.x, y: n.position.y },
      config: isRecord(config) ? config : undefined,
    };
  });
  const draftEdges: AgentWorkflowDraftEdge[] = edges.map((e) => ({
    id: e.id,
    type: typeof (e.data as Record<string, unknown> | undefined)?.edgeType === 'string'
      ? String((e.data as Record<string, unknown>).edgeType)
      : 'default',
    source: e.source,
    target: e.target,
    config: (() => {
      const currentConfig =
        isRecord((e.data as Record<string, unknown> | undefined)?.config)
          ? ((e.data as Record<string, unknown>).config as Record<string, unknown>)
          : {};
      const sourceSlot = typeof e.sourceHandle === 'string' ? e.sourceHandle : undefined;
      const targetSlot = typeof e.targetHandle === 'string' ? e.targetHandle : undefined;
      const merged = {
        ...currentConfig,
        ...(sourceSlot ? { sourceSlot } : {}),
        ...(targetSlot ? { targetSlot } : {}),
      };
      return Object.keys(merged).length > 0 ? merged : undefined;
    })(),
    priority:
      typeof (e.data as Record<string, unknown> | undefined)?.priority === 'number'
        ? Number((e.data as Record<string, unknown>).priority)
        : undefined,
  }));
  return {
    nodes: draftNodes,
    edges: draftEdges,
    meta: previousMeta && isRecord(previousMeta) ? { ...previousMeta } : {},
  };
}

export function draftGraphToRecord(graph: AgentWorkflowDraftGraph): Record<string, unknown> {
  return {
    nodes: graph.nodes,
    edges: graph.edges,
    ...(Object.keys(graph.meta ?? {}).length ? { meta: graph.meta } : {}),
  };
}

export function newNodeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `aw_${crypto.randomUUID()}`;
  }
  return `aw_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function newEdgeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `e_${crypto.randomUUID()}`;
  }
  return `e_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}
